import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { fetchAudioWithTimings } from '../../services/api';
import KaraokeText from '../KaraokeText';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';

const getSpeakerForGender = (gender) => (gender === 'female' ? 'af_sarah' : 'am_michael');

const formatTime = (seconds) => {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const Conversation = ({ conversation, topic, hideText = false, title = 'Přečtěte si konverzaci' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [currentLineIndex, setCurrentLineIndex] = useState(-1);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);

  const segmentsRef = useRef([]);
  const audioRef = useRef(null);
  const currentSegmentIdxRef = useRef(-1);
  const isPlayingRef = useRef(false);
  const animationFrameRef = useRef(null);
  const totalDurationRef = useRef(0);

  const cancelAnimation = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const updateLoop = useCallback(() => {
    const audio = audioRef.current;
    const segIdx = currentSegmentIdxRef.current;
    const seg = segmentsRef.current[segIdx];
    if (!audio || !seg || !isPlayingRef.current) return;

    const segTime = audio.currentTime;
    setCurrentTime(seg.startOffset + segTime);

    let wIdx = -1;
    for (let i = 0; i < seg.wordTimings.length; i++) {
      const t = seg.wordTimings[i];
      if (segTime >= t.start && segTime < t.end) {
        wIdx = i;
        break;
      }
    }
    setCurrentWordIndex(wIdx);

    animationFrameRef.current = requestAnimationFrame(updateLoop);
  }, []);

  const attachAudioForSegment = useCallback((idx, withinSegmentTime) => {
    const seg = segmentsRef.current[idx];
    if (!seg) return null;

    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
    }

    const audio = new Audio(seg.blobUrl);
    audio.preload = 'auto';
    audio.currentTime = withinSegmentTime;
    audio.onended = () => {
      const nextIdx = currentSegmentIdxRef.current + 1;
      if (nextIdx >= segmentsRef.current.length) {
        isPlayingRef.current = false;
        setIsPlaying(false);
        cancelAnimation();
        setCurrentTime(totalDurationRef.current);
        setCurrentWordIndex(-1);
        return;
      }
      attachAudioForSegment(nextIdx, 0);
      if (isPlayingRef.current && audioRef.current) {
        audioRef.current.play().catch((err) => console.error('Audio play error:', err));
      }
    };

    audioRef.current = audio;
    currentSegmentIdxRef.current = idx;
    setCurrentLineIndex(idx);
    return audio;
  }, []);

  const seekTo = useCallback(
    (time) => {
      const total = totalDurationRef.current;
      const clamped = Math.max(0, Math.min(time, total));
      const segs = segmentsRef.current;
      if (segs.length === 0) return;

      let idx = segs.length - 1;
      let within = segs[idx].duration;
      for (let i = 0; i < segs.length; i++) {
        if (clamped < segs[i].startOffset + segs[i].duration) {
          idx = i;
          within = Math.max(0, clamped - segs[i].startOffset);
          break;
        }
      }

      if (idx !== currentSegmentIdxRef.current) {
        attachAudioForSegment(idx, within);
      } else if (audioRef.current) {
        audioRef.current.currentTime = within;
      }

      setCurrentTime(clamped);

      // Refresh word highlight after seek
      const seg = segs[idx];
      let wIdx = -1;
      for (let i = 0; i < seg.wordTimings.length; i++) {
        const t = seg.wordTimings[i];
        if (within >= t.start && within < t.end) {
          wIdx = i;
          break;
        }
      }
      setCurrentWordIndex(wIdx);
    },
    [attachAudioForSegment]
  );

  const loadAudio = async () => {
    setIsLoading(true);
    try {
      const results = await Promise.all(
        conversation.map(({ text, gender }) =>
          fetchAudioWithTimings(text, getSpeakerForGender(gender))
        )
      );

      let offset = 0;
      const segs = results.map((r) => {
        const seg = {
          blobUrl: URL.createObjectURL(r.audioBlob),
          wordTimings: r.wordTimings || [],
          duration: r.duration || 0,
          startOffset: offset,
        };
        offset += seg.duration;
        return seg;
      });

      segmentsRef.current = segs;
      totalDurationRef.current = offset;
      setTotalDuration(offset);
      setIsLoaded(true);
      return segs;
    } catch (err) {
      console.error('Failed to load conversation audio:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const play = () => {
    if (segmentsRef.current.length === 0) return;
    isPlayingRef.current = true;
    setIsPlaying(true);

    // If finished, restart from beginning
    if (currentTime >= totalDurationRef.current - 0.01) {
      seekTo(0);
    } else if (!audioRef.current) {
      seekTo(currentTime);
    }

    if (audioRef.current) {
      audioRef.current.play().catch((err) => console.error('Audio play error:', err));
    }
    cancelAnimation();
    animationFrameRef.current = requestAnimationFrame(updateLoop);
  };

  const pause = () => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (audioRef.current) audioRef.current.pause();
    cancelAnimation();
  };

  const handleToggle = async () => {
    if (!isLoaded) {
      const segs = await loadAudio();
      if (!segs || segs.length === 0) return;
      // Initialize first segment then play
      attachAudioForSegment(0, 0);
      setCurrentTime(0);
      isPlayingRef.current = true;
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.play().catch((err) => console.error('Audio play error:', err));
      }
      cancelAnimation();
      animationFrameRef.current = requestAnimationFrame(updateLoop);
      return;
    }
    if (isPlaying) pause();
    else play();
  };

  const handleSeek = (e) => {
    if (!isLoaded || totalDurationRef.current <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const wasPlaying = isPlayingRef.current;
    if (wasPlaying && audioRef.current) audioRef.current.pause();
    seekTo(ratio * totalDurationRef.current);
    if (wasPlaying && audioRef.current) {
      audioRef.current.play().catch((err) => console.error('Audio play error:', err));
    }
  };

  const handleLineClick = (index) => {
    const seg = segmentsRef.current[index];
    if (!isLoaded || !seg) return;
    seekTo(seg.startOffset);
    if (!isPlayingRef.current) {
      play();
    } else if (audioRef.current) {
      audioRef.current.play().catch((err) => console.error('Audio play error:', err));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimation();
      if (audioRef.current) {
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.pause();
        audioRef.current = null;
      }
      segmentsRef.current.forEach((s) => {
        if (s.blobUrl) URL.revokeObjectURL(s.blobUrl);
      });
      segmentsRef.current = [];
    };
  }, []);

  const progressPercent = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <ExerciseCard>
      <h2 className="text-2xl font-bold text-gray-800 border-b-4 border-blue-500 pb-4 mb-4">
        {title}
      </h2>

      <div className="flex items-center gap-3 mb-5 bg-gray-50 rounded-xl px-4 py-3">
        <button
          onClick={handleToggle}
          disabled={isLoading}
          className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            isLoading
              ? 'bg-gray-300 text-gray-500 cursor-wait'
              : 'bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer'
          }`}
          aria-label={isPlaying ? 'Pauza' : 'Přehrát'}
        >
          {isLoading ? (
            <Loader2 size={22} className="animate-spin" />
          ) : isPlaying ? (
            <Pause size={22} />
          ) : (
            <Play size={22} className="ml-0.5" />
          )}
        </button>

        <div className="flex-1 flex items-center gap-3">
          <span className="text-sm font-mono text-gray-600 tabular-nums w-12 text-right">
            {formatTime(currentTime)}
          </span>
          <div
            onClick={handleSeek}
            className={`flex-1 h-2 bg-gray-200 rounded-full relative ${
              isLoaded ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            <div
              className="absolute left-0 top-0 h-full bg-cyan-500 rounded-full pointer-events-none"
              style={{ width: `${progressPercent}%` }}
            />
            {isLoaded && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white border-2 border-cyan-500 rounded-full shadow pointer-events-none"
                style={{ left: `calc(${progressPercent}% - 7px)` }}
              />
            )}
          </div>
          <span className="text-sm font-mono text-gray-600 tabular-nums w-12">
            {formatTime(totalDuration)}
          </span>
        </div>
      </div>

      {hideText ? (
        <div className="text-center text-gray-600 bg-gray-50 rounded-xl px-4 py-8 text-lg leading-relaxed">
          🎧 Poslouchej dialog a odpověz na otázky.<br />
          Text se ukáže, až otázky dokončíš.
        </div>
      ) : (
      <div className="text-lg leading-relaxed">
        {conversation.map(({ role, text, gender }, index) => {
          const seg = segmentsRef.current[index];
          const showKaraoke = currentLineIndex === index && seg && seg.wordTimings.length > 0;
          return (
            <div
              key={index}
              className={`flex items-start gap-2 px-3 py-2 rounded-lg mb-1 transition-colors ${
                currentLineIndex === index ? 'bg-blue-50' : ''
              }`}
            >
              <button
                onClick={() => handleLineClick(index)}
                disabled={!isLoaded}
                className={`shrink-0 w-6 h-6 mt-1 rounded-full flex items-center justify-center transition-all ${
                  isLoaded
                    ? 'bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                aria-label={`Přehrát řádek: ${role}`}
                title="Přehrát tento řádek"
              >
                <Play size={12} className="ml-0.5" />
              </button>
              <div className="flex-1">
                <strong
                  className={`text-xl ${gender === 'female' ? 'text-rose-600' : 'text-blue-600'}`}
                >
                  {role}:
                </strong>{' '}
                {showKaraoke ? (
                  <KaraokeText
                    wordTimings={seg.wordTimings}
                    currentWordIndex={currentWordIndex}
                    textColor="#374151"
                    highlightColor="#fef3c7"
                  />
                ) : (
                  <TranslatableText text={text} textColor="#374151" topic={topic} />
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}
    </ExerciseCard>
  );
};

export default Conversation;
